'use client'

import { useEffect, useState } from 'react'
import { Box, Image, Text, Flex } from '@chakra-ui/react'
import { Button } from '@/components/ui/button'
import { usePathname } from 'next/navigation'

export const APP_BANNER_HEIGHT = 56

export default function SmartAppBanner({
  bannerShowing,
  position,
}: {
  bannerShowing?: (showing: boolean) => void
  position?: 'fixed' | 'relative'
}) {
  const [show, setShow] = useState(false)
  const [isAndroid, setIsAndroid] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  const pathname = usePathname()

  const isDirectDeepLink =
    pathname?.startsWith('/l/') || pathname?.startsWith('/c/')

  // ✅ Device detection (SAFE)
  useEffect(() => {
    if (typeof navigator === 'undefined') return

    const ua = navigator.userAgent

    setIsAndroid(/Android/i.test(ua))
    setIsIOS(/iP(hone|ad|od)/i.test(ua))
  }, [])
  const isIOSNotSafari = () => {
    const ua = navigator.userAgent

    const isIOS = /iP(hone|ad|od)/i.test(ua)
    const isSafari =
      /Safari/i.test(ua) &&
      !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(ua)

    return isIOS && !isSafari
  }
  // ✅ Banner visibility logic
  useEffect(() => {
    if (isIOSNotSafari() || isAndroid) {
      setShow(true)
      bannerShowing?.(true)
    }
  }, [isDirectDeepLink, bannerShowing, isAndroid])

  useEffect(() => {
    if (position === 'relative') return
    if (typeof document === 'undefined') return

    const height = show ? `${APP_BANNER_HEIGHT}px` : '0px'

    document.documentElement.style.setProperty(
      '--app-banner-height',
      height
    )

    return () => {
      document.documentElement.style.setProperty(
        '--app-banner-height',
        '0px'
      )
    }
  }, [position, show])
  
  const openApp = () => {
    const playStoreUrl =
      'https://play.google.com/store/apps/details?id=com.lykstage.app'

    const appStoreUrl =
      'https://apps.apple.com/us/app/lykstage-plus/id6754064834'

    // ================= ANDROID =================
    if (isAndroid) {
      const ua = navigator.userAgent
      const isChrome =
        /Chrome/i.test(ua) && !/Edg|OPR|Brave/i.test(ua)

      if (!isChrome) {
        window.location.href = isDirectDeepLink
          ? pathname
          : 'https://www.lykstage.com/open'
      } else {
        const packageName = 'com.lykstage.app'
        const intentUrl = `intent://www.lykstage.com${pathname}#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${playStoreUrl};end;`
        window.location.href = isDirectDeepLink
          ? intentUrl
          : 'https://www.lykstage.com/open'
      }
      return
    }

    // ================= iOS =================
    if (isIOSNotSafari()) {
      const start = Date.now()
      // Try to open app
      window.location.href = `lykstage://${pathname}`

      // App Store fallback
      setTimeout(() => {
        if (Date.now() - start < 2500 && !document.hidden) {
          window.location.href = appStoreUrl
        }
      }, 2000)
    }
  }

  if (!show) return null

  return (
    <Box
      position={position || 'fixed'}
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      bg="#F5F5F7"
      borderBottom="1px solid"
      borderColor="#D1D1D6"
      height={`${APP_BANNER_HEIGHT}px`}
      px="12px"
    >
      <Flex align="center" height="100%" gap="10px">
        <Image
          src="/icons/lykstage-app.png"
          alt="Lykstage"
          boxSize="40px"
          borderRadius="9px"
        />

        <Box flex="1">
          <Text fontSize="14px" fontWeight="600">
            LYKSTAGE
          </Text>
          <Text fontSize="12px" color="#6E6E73">
            Open in LYKSTAGE APP
          </Text>
        </Box>

        <Button
          size="sm"
          onClick={openApp}
          style={{
            backgroundColor: '#24bfa3',
            color: '#fff',
            borderRadius: '14px',
            padding: '6px 14px',
            fontWeight: 600,
          }}
        >
          OPEN
        </Button>
      </Flex>
    </Box>
  )
}
